import { ChangeDetectorRef, Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Permission } from '@/apiConnection/model/user';
import { Urls } from '@/apiConnection/urls';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { LoadingComponent } from '@/components/loading/loading.component';

interface DashboardKpis {
  revenue_month: { value: number; delta_pct: number | null };
  active_orders: { value: number; new_last_7_days: number };
  low_stock: { value: number; threshold: number };
  new_clients: { value: number; delta_pct: number | null };
}

interface DashboardPermissions {
  documents: boolean;
  products: boolean;
  clients: boolean;
  activity: boolean;
}

interface RevenueByCategoryRow {
  label: string;
  revenue: number;
  pct: number;
}

interface RecentActivityRow {
  action: string;
  method: string;
  path: string;
  user_name: string | null;
  created_at: string;
}

interface QuickAction {
  icon: string;
  label: string;
  link: string;
  panel?: string;
  action?: keyof Permission;
  superadminOnly?: boolean;
}

interface BarGroup {
  label: string;
  curX: number; curY: number; curH: number;
  prevX: number; prevY: number; prevH: number;
  labelX: number;
}

interface DonutSegment {
  dasharray: string;
  dashoffset: number;
  color: string;
}

const CHART_W = 640;
const BAR_H = 220;
const LINE_H = 200;
const DONUT_SIZE = 160;
const DONUT_R = 62;
const DONUT_STROKE = 24;
const DONUT_COLORS = ['#2563eb', '#60a5fa', '#a5b4fc', '#c7d2fe', '#93c5fd', '#bae6fd'];

@Component({
  imports: [RouterModule, CentToEurDisplayDirective, LoadingComponent],
  templateUrl: './dashboard.page.html',
  styleUrl: './dashboard.page.scss',
})
export class DashboardPage {
  loading = true;

  kpis: DashboardKpis | null = null;
  permissions: DashboardPermissions = { documents: false, products: false, clients: false, activity: false };
  recentActivity: RecentActivityRow[] = [];
  revenueByCategory: (RevenueByCategoryRow & { color: string })[] = [];

  barGroups: BarGroup[] = [];
  barGridLines: number[] = [0, 1, 2, 3].map(i => BAR_H - 24 - (i / 3) * (BAR_H - 34));

  linePath = '';
  lineArea = '';
  lineDots: { x: number; y: number }[] = [];
  lineLabels: { x: number; label: string }[] = [];
  lineGridLines: number[] = [0, 1, 2, 3].map(i => LINE_H - 22 - (i / 3) * (LINE_H - 36));

  donutSegments: DonutSegment[] = [];
  donutTotalLabel = '';

  readonly chartW = CHART_W;
  readonly barH = BAR_H;
  readonly lineH = LINE_H;
  readonly donutSize = DONUT_SIZE;
  readonly donutR = DONUT_R;
  readonly donutStroke = DONUT_STROKE;
  readonly donutCx = DONUT_SIZE / 2;
  readonly donutCy = DONUT_SIZE / 2;

  private readonly allQuickActions: QuickAction[] = [
    { icon: 'cash-register.svg', label: 'Nueva factura', link: '/sales/new/invoice', panel: 'documents', action: 'can_create' },
    { icon: 'euro.svg', label: 'Nuevo presupuesto', link: '/budgets/new/budget', panel: 'documents', action: 'can_create' },
    { icon: 'boxes-stacked.svg', label: 'Nuevo producto', link: '/products', panel: 'products', action: 'can_create' },
    { icon: 'users.svg', label: 'Nuevo cliente', link: '/clients', panel: 'clients', action: 'can_create' },
    { icon: 'truck.svg', label: 'Nuevo proveedor', link: '/providers', panel: 'providers', action: 'can_create' },
    { icon: 'shop-cart.svg', label: 'Orden de compra', link: '/purchase/new/invoice', panel: 'purchases', action: 'can_create' },
    { icon: 'inform.svg', label: 'Informe de existencias', link: '/stock-report', panel: 'products' },
    { icon: 'cubes.svg', label: 'Historial de inventario', link: '/inventory-history', panel: 'history_inventory' },
  ];

  private readonly entityLabels: Record<string, string> = {
    clients: 'Clientes',
    providers: 'Proveedores',
    categories: 'Familias',
    products: 'Productos',
    documents: 'Documentos',
    purchases: 'Compras',
    'pay-methods': 'Formas de pago',
    measurements: 'Medidas',
    offices: 'Oficinas',
    'history-inventory': 'Historial de inventario',
    users: 'Usuarios',
    roles: 'Roles',
    companies: 'Compañías',
    'templates-settings': 'Plantillas',
  };

  private readonly actionVerbs: Record<string, string> = {
    create: 'creó un registro en',
    update: 'actualizó un registro en',
    delete: 'eliminó un registro en',
  };

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    public authService: AuthenticationService,
  ) { }

  get quickActions(): QuickAction[] {
    return this.allQuickActions.filter(qa => {
      if (qa.superadminOnly && !this.authService.isSuperAdmin) return false;
      if (qa.panel && !this.authService.hasPanel(qa.panel, qa.action ?? 'can_read')) return false;
      return true;
    });
  }

  get showKpiSection(): boolean {
    return this.permissions.documents || this.permissions.products || this.permissions.clients;
  }

  get showTrendRow(): boolean {
    return this.permissions.documents || this.permissions.activity;
  }

  get currentDateLabel(): string {
    const formatted = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  ngOnInit(): void {
    this.loading = true;
    this.apiService.getPetition(Urls.urls.dashboard).subscribe({
      next: (res: any) => {
        this.kpis = res.kpis;
        this.permissions = res.permissions ?? { documents: false, products: false, clients: false, activity: false };
        this.recentActivity = res.recent_activity ?? [];
        this.buildRevenueByCategory(res.revenue_by_category ?? []);
        this.buildBarChart(res.sales_by_month);
        this.buildLineChart(res.orders_trend);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  activityLabel(row: RecentActivityRow): string {
    const segment = row.path.replace(/^\/?api\//, '').split('/')[0];
    const entity = this.entityLabels[segment] ?? segment;
    const verb = this.actionVerbs[row.action] ?? 'modificó un registro en';
    const who = row.user_name ?? 'Un usuario';
    return `${who} ${verb} ${entity}`;
  }

  timeAgo(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Hace un momento';
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
  }

  private buildRevenueByCategory(rows: RevenueByCategoryRow[]): void {
    this.revenueByCategory = rows.map((row, i) => ({ ...row, color: DONUT_COLORS[i % DONUT_COLORS.length] }));

    const circumference = 2 * Math.PI * DONUT_R;
    let offset = 0;
    this.donutSegments = this.revenueByCategory.map(row => {
      const len = (row.pct / 100) * circumference;
      const seg: DonutSegment = { dasharray: `${len} ${circumference - len}`, dashoffset: -offset, color: row.color };
      offset += len;
      return seg;
    });

    const totalCents = rows.reduce((sum, r) => sum + r.revenue, 0);
    this.donutTotalLabel = (totalCents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  }

  private buildBarChart(data: { months: string[]; current_year: number[]; previous_year: number[] } | undefined): void {
    if (!data) { this.barGroups = []; return; }

    const padL = 30, padB = 24, padT = 10;
    const chartW = CHART_W - padL - 10, chartH = BAR_H - padB - padT;
    const groupW = chartW / data.months.length;
    const barW = groupW * 0.32;
    const max = Math.max(1, ...data.current_year, ...data.previous_year);

    this.barGroups = data.months.map((label, i) => {
      const gx = padL + i * groupW;
      const hPrev = (data.previous_year[i] / max) * chartH;
      const hCur = (data.current_year[i] / max) * chartH;
      return {
        label,
        prevX: gx + groupW * 0.18, prevY: padT + chartH - hPrev, prevH: hPrev,
        curX: gx + groupW * 0.18 + barW + 4, curY: padT + chartH - hCur, curH: hCur,
        labelX: gx + groupW / 2,
      };
    });
  }

  private buildLineChart(data: { months: string[]; counts: number[] } | undefined): void {
    if (!data || data.counts.length === 0) { this.linePath = ''; this.lineArea = ''; this.lineDots = []; this.lineLabels = []; return; }

    const padL = 30, padR = 10, padB = 22, padT = 14;
    const chartW = CHART_W - padL - padR, chartH = LINE_H - padB - padT;
    const max = Math.max(1, ...data.counts);
    const min = 0;

    const pts = data.counts.map((v, i) => {
      const x = padL + (i / (data.counts.length - 1)) * chartW;
      const y = padT + chartH - ((v - min) / (max - min || 1)) * chartH;
      return [x, y];
    });

    this.linePath = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
    this.lineArea = this.linePath + ` L${pts[pts.length - 1][0]},${padT + chartH} L${pts[0][0]},${padT + chartH} Z`;
    this.lineDots = pts.map(([x, y]) => ({ x, y }));
    this.lineLabels = data.months.map((label, i) => ({ x: pts[i][0], label }));
  }
}
